import { Button } from "@legal-500/legal500-design-system";
import { APPROVAL_STATUS_TARGETS, countByStatus } from "./approvalRequestStatus.js";
import { DUPLICATE_REASON, downloadInvalidRowsCsv } from "./approvalRequestStatusCsv.js";
import type { ParsedApprovalRequestStatusCsv } from "./parseApprovalRequestStatusCsv.js";
import styles from "../PipelineBrowser.module.css";

const DISPLAY_CAP = 50;

type ApprovalRequestStatusParsedSummaryProps = {
  parsed: ParsedApprovalRequestStatusCsv;
  fileName: string;
};

export function ApprovalRequestStatusParsedSummary({
  parsed,
  fileName,
}: ApprovalRequestStatusParsedSummaryProps) {
  const counts = countByStatus(parsed.rows);
  const breakdown = APPROVAL_STATUS_TARGETS.map(status => `${status} ${counts[status]}`).join(", ");
  const problems = [
    ...parsed.invalid.map(row => ({
      key: `line-${row.line}`,
      label: `Line ${row.line}: ${row.approvalRequestId || "(empty)"} / ${row.status || "(empty)"} — ${row.reason}`,
    })),
    ...parsed.duplicates.map(id => ({ key: `dup-${id}`, label: `${id} — ${DUPLICATE_REASON}` })),
  ];
  const shown = problems.slice(0, DISPLAY_CAP);
  const hidden = problems.length - shown.length;

  return (
    <>
      <div className={styles.syncStateMessage}>
        {fileName}: <strong>{parsed.rows.length}</strong> valid rows ({breakdown}),{" "}
        <strong>{parsed.invalid.length}</strong> invalid,{" "}
        <strong>{parsed.duplicates.length}</strong> duplicates
      </div>
      {parsed.duplicates.length > 0 && (
        <div className={styles.mappingError}>
          Duplicate approval_request_id values must be removed before this file can be run.
        </div>
      )}
      {problems.length > 0 && (
        <>
          <ul className={styles.problemList}>
            {shown.map(problem => (
              <li key={problem.key}>{problem.label}</li>
            ))}
          </ul>
          <div className={styles.syncStateRow}>
            {hidden > 0 && <span>…and {hidden} more.</span>}
            <Button
              variant="outlined"
              color="secondary"
              size="small"
              onClick={() => downloadInvalidRowsCsv(parsed.invalid, parsed.duplicates, new Date())}
            >
              Download invalid rows CSV
            </Button>
          </div>
        </>
      )}
    </>
  );
}
